// RITA-1515 (Nutzungserfassung Phase 3a, Epic RITA-1496) — Ladeleistung je Seite zusammenfalten.
//
// Die Lese-API (usageVitals) liefert eine Zeile je (Seite, Kennzahl), weil die Schutzregeln je
// Gruppe gelten: eine Seite kann für LCP zeigbar sein (jede Seitenladung misst es) und für INP
// nicht (nur wer bedient hat, liefert eines). Die Tabelle zeigt trotzdem eine Zeile je Seite —
// vier Kennzahlen nebeneinander lesen sich, vier Zeilen je Seite nicht.
//
// CONTRACT:
//  1. Hier wird NICHTS gerechnet, nur umsortiert. Median, 95 % und Textmarke kommen unverändert
//     vom Server (usageFormat zeigt sie an).
//  2. Nutzerzahlen werden NICHT addiert — die Schnittmenge der Pseudonyme ist unbekannt. Die
//     Zeile zeigt die größte Nutzerzahl ihrer Kennzahlen; ist eine davon nur eine Untergrenze
//     (usersEstimated), gilt das für die Zeile.
//  3. Unterdrückte Zeilen bleiben unterdrückt: alle Sammelzeilen werden zu EINER Sammelzeile am
//     Ende OHNE Zellen. Eine Sammelzeile mit Median wäre eine Aussage über die verborgenen Seiten.
//     groups summiert sich, users ist die größte Zahl (0, wenn alle 0 sind — „aus
//     Datenschutzgründen nichts anzeigbar“).
//  4. Die Reihenfolge des Servers bleibt: eine Seite steht dort, wo sie zuerst vorkommt (der
//     Server sortiert nach Anzahl absteigend).
//  5. „Ladevorgänge“ ist die Zahl der LCP-Messungen: LCP gibt es genau einmal je echtem Laden
//     der Anwendung. Ohne LCP-Zelle bleibt die Zahl null (nicht 0).
import type { UsageVitalMetric, UsageVitalRow } from '../types';

/** Feste Reihenfolge der Kennzahlen in Kopfzeile und Zellen. */
export const VITAL_METRICS: readonly UsageVitalMetric[] = ['LCP', 'INP', 'CLS', 'TTFB'];

export interface VitalZelle {
  count: number;
  p50: number | null;
  p95: number | null;
  p95Note: string | null;
}

export interface VitalZeile {
  /** Routen-Muster der Seite; null = Sammelzeile. */
  route: string | null;
  suppressed: boolean;
  groups: number;
  users: number;
  usersEstimated: boolean;
  /** Zahl der LCP-Messungen (= echte Ladevorgänge); null ohne LCP-Zelle. */
  ladevorgaenge: number | null;
  zellen: Partial<Record<UsageVitalMetric, VitalZelle>>;
}

const istKennzahl = (metric: string | null): metric is UsageVitalMetric =>
  metric !== null && (VITAL_METRICS as readonly string[]).includes(metric);

export const pivotVitals = (rows: readonly UsageVitalRow[]): VitalZeile[] => {
  const seiten = new Map<string, VitalZeile>();
  let sammel: VitalZeile | null = null;

  rows.forEach((row) => {
    if (row.suppressed || row.route === null) {
      if (sammel === null) {
        sammel = {
          route: null,
          suppressed: true,
          groups: 0,
          users: 0,
          usersEstimated: false,
          ladevorgaenge: null,
          zellen: {},
        };
      }
      sammel.groups += row.groups;
      sammel.users = Math.max(sammel.users, row.users);
      sammel.usersEstimated = sammel.usersEstimated || row.usersEstimated;
      return;
    }
    if (!istKennzahl(row.metric)) return;

    let zeile = seiten.get(row.route);
    if (zeile === undefined) {
      zeile = {
        route: row.route,
        suppressed: false,
        groups: 1,
        users: 0,
        usersEstimated: false,
        ladevorgaenge: null,
        zellen: {},
      };
      seiten.set(row.route, zeile);
    }
    zeile.users = Math.max(zeile.users, row.users);
    zeile.usersEstimated = zeile.usersEstimated || row.usersEstimated;
    zeile.zellen[row.metric] = { count: row.count, p50: row.p50, p95: row.p95, p95Note: row.p95Note };
    if (row.metric === 'LCP') zeile.ladevorgaenge = row.count;
  });

  const ergebnis = Array.from(seiten.values());
  if (sammel !== null) ergebnis.push(sammel);
  return ergebnis;
};
